// src/encounter/services/use-remove-invoice-line.ts
// The wrap-up invoice row ×. Order to Invoice (pm-react T9): a line that came from a pending order
// goes BACK to Pending orders through `Orders/revert`, so removing it never loses the order
// (Andrew, 8 Oct). Any other line keeps the plain AddRemoveService remove, which the host owns
// (its mutation also drives the panel's busy flag and the add path).
//
// Errors: `useRevertOrders` toasts every failure itself (the contract message, or the generic one)
// and re-reads both keys on a 409, so there is no `onError` here.
import { invoiceLineRemoval } from '@/billing/lib/pending-orders';
import { useRevertOrders } from '@/billing/services/use-invoice-orders';
import { showSuccess } from '@/shared/lib/toast-helpers';
import type { InvoiceService } from '@/shared/types/billing';

export interface UseRemoveInvoiceLineArgs {
  invoiceId: string | null | undefined;
  /** The invoice's current lines (to tell an order line from a manual one). */
  services: readonly InvoiceService[] | undefined;
  /** The plain remove (`useAddRemoveService` → `removeServices: [lineId]`). */
  removeService: (lineId: string) => void;
}

export function useRemoveInvoiceLine({ invoiceId, services, removeService }: UseRemoveInvoiceLineArgs) {
  const revertOrders = useRevertOrders();
  const remove = (lineId: string) => {
    if (!invoiceId) return;
    const removal = invoiceLineRemoval(services, lineId);
    if (removal.kind === 'revert') {
      revertOrders.mutate(
        { invoiceId, request: { items: [{ sourceOrderKey: removal.sourceOrderKey }] } },
        { onSuccess: () => showSuccess(`${removal.name} returned to pending orders`) },
      );
      return;
    }
    removeService(lineId);
  };
  return { remove, isPending: revertOrders.isPending };
}
